import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import Envelope from '@deliveroo/icon-set/react/Envelope';
import Facebook from '@deliveroo/icon-set/react/Facebook';
import Twitter from '@deliveroo/icon-set/react/Twitter';
import Icon from '@deliveroo/consumer-component-library/components/Icon';
import SpacerElement from '@deliveroo/consumer-component-library/internal/SpacerElement';
import styles from './SocialButton.scss';

/**
 * A button component to use for social network sharing.
 */
export default function SocialButton(props) {
  let Element = 'button';
  let additionalProps = {};

  const labelFromType = (type) => {
    switch (type) {
      case 'email':
        return 'Email';
      case 'twitter':
        return 'Twitter';
      case 'facebook':
        return 'Facebook';
      default:
        return null;
    }
  };

  const iconFromType = (type) => {
    switch (type) {
      case 'email':
        return Envelope;
      case 'twitter':
        return Twitter;
      case 'facebook':
        return Facebook;
      default:
        return null;
    }
  };

  const label = props.label ? props.label : labelFromType(props.type);

  const wrapperClasses = classnames([
    styles.wrapper,
    {
      [styles.isBlock]: props.isBlock,
    },
  ]);

  const buttonClasses = classnames(styles.button, styles[props.type], {
    [styles.isBlock]: props.isBlock,
  });

  const onClick = (event) => {
    if (props.onClick) {
      props.onClick(event);
    }
  };

  if (props.href) {
    Element = 'a';

    additionalProps = {
      ...additionalProps,
      href: props.href,
      rel: 'noopener noreferrer',
      target: '_blank',
    };
  } else {
    additionalProps = {
      ...additionalProps,
      type: 'button',
    };
  }

  /* eslint-disable react/prop-types */
  const { spacing, adjacentVerticalSpacing, adjacentHorizontalSpacing } = props;
  /* eslint-enable react/prop-types */

  return (
    <SpacerElement
      spacing={spacing}
      adjacentVerticalSpacing={adjacentVerticalSpacing}
      adjacentHorizontalSpacing={adjacentHorizontalSpacing}
      className={wrapperClasses}
      element="span"
    >
      <Element className={buttonClasses} onClick={onClick} {...additionalProps} tabIndex={0}>
        <span className={styles.buttonContent}>
          <Icon
            component={iconFromType(props.type)}
            spacing={[null, 'small', null, null]}
            size={24}
          />
          <span className={styles.buttonLabel}>{label}</span>
        </span>
      </Element>
    </SpacerElement>
  );
}

SocialButton.propTypes = {
  /** The type of button, more on that later. */
  type: PropTypes.oneOf(['email', 'twitter', 'facebook']),

  /** The string label for the button. Defaults to the name of the social network. */
  label: PropTypes.string,

  /** Whether the button should be full width or not. */
  isBlock: PropTypes.bool,

  /** If provided will output an `<a>` tag vs a `<button>`. */
  href: PropTypes.string,

  /** Event handlers. */
  onClick: PropTypes.func,
};

SocialButton.defaultProps = {
  type: 'primary',
  label: null,
  isBlock: false,
  href: '',
  onClick: null,
};
